import { normalizeVoiceText, parseVoiceCommand } from "./voiceControl";

// Comandos de voz de la repeticion. Usa el mismo motor (VoiceControlController) y
// los mismos comandos de camara que la partida; aqui solo se anaden los del replay.

const UNITS = {
  cero: 0, un: 1, uno: 1, una: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6, siete: 7,
  ocho: 8, nueve: 9, diez: 10, once: 11, doce: 12, trece: 13, catorce: 14, quince: 15,
  dieciseis: 16, diecisiete: 17, dieciocho: 18, diecinueve: 19, veinte: 20, veintiun: 21,
  veintiuno: 21, veintiuna: 21, veintidos: 22, veintitres: 23, veinticuatro: 24,
  veinticinco: 25, veintiseis: 26, veintisiete: 27, veintiocho: 28, veintinueve: 29,
};
const TENS = {
  treinta: 30, cuarenta: 40, cincuenta: 50, sesenta: 60, setenta: 70, ochenta: 80, noventa: 90,
};
const HUNDREDS = {
  cien: 100, ciento: 100, doscientos: 200, doscientas: 200, trescientos: 300, trescientas: 300,
};

// Lee un numero (en cifra o en palabras) al principio de la lista de palabras.
const readNumber = (words) => {
  if (!words.length) return null;
  if (/^\d+$/.test(words[0])) return Number(words[0]);
  let total = 0;
  let used = false;
  let index = 0;
  if (HUNDREDS[words[index]] !== undefined) {
    total += HUNDREDS[words[index]];
    used = true;
    index += 1;
  }
  if (TENS[words[index]] !== undefined) {
    total += TENS[words[index]];
    used = true;
    index += 1;
    if (words[index] === "y" && UNITS[words[index + 1]] !== undefined && UNITS[words[index + 1]] < 10) {
      total += UNITS[words[index + 1]];
    }
  } else if (UNITS[words[index]] !== undefined) {
    total += UNITS[words[index]];
    used = true;
  }
  return used ? total : null;
};

const findNumberAfter = (text, pattern) => {
  const match = text.match(pattern);
  if (!match) return null;
  const rest = text.slice(match.index + match[0].length).trim().split(" ").filter(Boolean);
  return readNumber(rest);
};

const has = (text, phrases) => {
  const padded = ` ${text} `;
  return phrases.some((phrase) => padded.includes(` ${phrase} `));
};

const command = (id, label, extra = {}) => ({ id, type: "replay", label, keyData: null, ...extra });

const GAME_VOICE_TYPES = new Set(["camera", "rotation", "board_move"]);

const COLOR_WORDS = {
  azul: "azul", azules: "azul", rojo: "rojo", roja: "rojo", rojos: "rojo",
  amarillo: "amarillo", amarilla: "amarillo", verde: "verde", verdes: "verde",
};

// Propios del replay: siguiente, atras, reiniciar, play, pausa, ir a la jugada N,
// velocidad de los bloques, analiza la partida, tomar el control / continuar.
// Lo demas son los comandos de la partida (camara y mover/incorporar bloques).
export const parseReplayVoiceCommand = (transcript) => {
  const text = normalizeVoiceText(transcript)
    .replace(/^(?:oye|ok|vale|bueno|por favor|xima)\s+/, "")
    .replace(/\s+(?:por favor|gracias)$/, "")
    .trim();
  if (!text) return null;
  const done = (entry) => ({ ...entry, transcript: text });

  if (
    has(text, ["desactivar voz", "desactiva voz", "desactivar la voz", "desactiva la voz", "apagar voz",
      "apaga voz", "apagar la voz", "apaga la voz", "apagar microfono", "apaga microfono",
      "apagar el microfono", "apaga el microfono", "desactivar control de voz",
      "desactiva el control de voz", "desactivar el control de voz"])
  ) {
    return done(command("voice_off", "Apagar control de voz"));
  }
  if (has(text, ["ver comandos", "lista de comandos", "comandos", "ayuda"])) {
    return done(command("voice_help", "Ver comandos"));
  }

  // Ve a la jugada N (el id lleva el numero para que «jugada 1» y «jugada 12» no se tomen por repetidos).
  const gotoNumber = findNumberAfter(text, /\b(?:jugada|jugadas|movimiento)(?:\s+(?:numero|n))?\b/);
  if (gotoNumber !== null) {
    return done(command(`goto:${gotoNumber}`, `Jugada ${gotoNumber}`, { step: gotoNumber }));
  }

  if (has(text, ["tomar el control", "toma el control", "tomo el control", "tomar control", "toma control"])) {
    return done(command("take_control", "Tomar el control"));
  }
  if (has(text, ["restablecer", "restablece", "restablecelo", "continuar con la partida", "continua con la partida",
    "continuar la partida", "continua la partida", "seguir con la partida", "sigue con la partida"])) {
    return done(command("continue_game", "Continuar con la partida"));
  }
  if (has(text, ["deshacer", "deshaz", "deshace"])) return done(command("analysis_undo", "Deshacer"));
  if (/\b(?:analiza|analizar|analice|analisis)\b/.test(text) && has(text, ["partida", "analisis"])) {
    return done(command("review_on", "Analizar partida"));
  }

  const faster = has(text, ["mas rapido", "acelera", "acelerar", "aumenta", "aumentar", "sube", "subir"]);
  const slower = has(text, ["mas lento", "mas despacio", "disminuye", "disminuir", "reduce", "reducir", "baja", "bajar", "frena"]);
  if ((faster || slower) && has(text, ["velocidad", "rapido", "lento", "despacio", "acelera", "acelerar", "frena"])) {
    return done(command(faster ? "speed_up" : "speed_down", faster ? "Más rápido" : "Más lento", { type: "speed" }));
  }

  if (has(text, ["reiniciar", "reinicia", "reinicio", "reiniciar partida", "reinicia la partida"])) {
    return done(command("restart", "Reiniciar"));
  }
  if (has(text, ["siguiente", "siguiente jugada"])) return done(command("next", "Siguiente"));
  if (has(text, ["atras", "anterior", "jugada anterior"])) return done(command("previous", "Atrás"));
  if (has(text, ["pausa", "pausar", "detener", "deten", "detente", "stop", "estop", "alto", "basta", "pare", "parale"]) ||
      /(?:^| )para$/.test(text)) {
    return done(command("pause", "Pausa"));
  }
  if (has(text, ["play", "juega", "jugar", "reproducir", "reproduce", "plei"])) {
    return done(command("play", "Play"));
  }

  // «desde H5» responde a «¿Desde qué casilla?».
  const fromMatch = text.match(/^(?:desde|de)\s+(?:la\s+(?:casilla\s+)?)?(.+)$/);
  if (fromMatch) {
    const origin = parseVoiceCommand(`mueve de ${fromMatch[1]}`);
    if (origin && String(origin.id).startsWith("move_origin:")) {
      return { ...origin, label: `Desde ${origin.id.split(":")[1].toUpperCase()}`, transcript: text };
    }
  }

  // Comandos de la partida: gira, izquierda, derecha, 45, arriba, abajo, acercar, alejar,
  // centro, esquina, cara jugador 1-4 («cara de jugador 2» = «cara jugador 2»), mueve,
  // incorpora...
  const gameText = text.replace(/\bcara del? jugador\b/g, "cara jugador").replace(/\bcara de la?\b/g, "cara");
  let gameCommand = parseVoiceCommand(gameText);
  if (gameCommand?.type === "board_move") {
    const id = String(gameCommand.id);
    // «pon / coloca un bloque en F5» = incorporar.
    if (id.startsWith("board_hint_cell:") && has(text, ["pon", "pone", "ponga", "coloca", "colocar", "coloque"])) {
      gameCommand = { ...gameCommand, id: id.replace("board_hint_cell:", "incorporate_cell:") };
    }
    // «incorpora un bloque azul en F5»: el color elige el jugador.
    const color = text.split(" ").map((word) => COLOR_WORDS[word]).find(Boolean);
    if (color && /^incorporate(?:_special)?_cell:/.test(String(gameCommand.id))) {
      gameCommand = { ...gameCommand, id: `${gameCommand.id}:${color}` };
    }
  }
  if (gameCommand && (GAME_VOICE_TYPES.has(gameCommand.type) || String(gameCommand.id).startsWith("face_player:"))) {
    // Sin teclas: en la repeticion las flechas del teclado cambian de jugada.
    // La etiqueta repite lo que se dijo (los giros de la partida estan invertidos a proposito).
    const label = String(transcript || "").trim() || gameCommand.label;
    return { ...gameCommand, label: label.charAt(0).toUpperCase() + label.slice(1), keyData: null, transcript: text };
  }
  return null;
};

export const REPLAY_VOICE_HELP = [
  ["Siguiente", "Atrás"],
  ["Play · Juega", "Pausa · Para · Detente · Stop"],
  ["Reiniciar", "Ve a la jugada 12"],
  ["Aumenta la velocidad de los bloques", "Disminuye la velocidad"],
  ["Analiza la partida", "Tomar el control"],
  ["Gira · Izquierda · Derecha", "Más (45°) · Menos · Media vuelta"],
  ["Arriba · Abajo", "Acercar · Alejar"],
  ["Centro · Esquina", "Cara de jugador 1 a 4"],
  ["Con el control: incorpora un bloque en F5", "Mueve el bloque de H5 a F4"],
  ["Mueve un bloque a B3 (luego «desde H5»)", "Deshacer · Continuar con la partida"],
  ["Seguir cámara · Cámara libre", "Vista superior · Vista frontal"],
  ["Enfoca el bloque especial", "Primera / segunda / tercera planta · La cima"],
  ["Jugador 2 · Segundo jugador", "Más / menos = 45°"],
  ["Apagar micrófono", "Ver comandos"],
];
